import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CreditCard, KeyRound, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PasswordInput, fieldCls, labelCls } from "@/components/FormBits";
import { Input } from "@/components/ui/input";

function Section({ icon: Icon, title, sub, children, testId }) {
  return (
    <section className="rounded-3xl border border-[#E6DBCF] bg-white/70 p-6 sm:p-8" data-testid={testId}>
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F6F1EA] text-[#9C6218]"><Icon size={18} /></span>
        <div className="min-w-0">
          <h2 className="font-serif text-2xl font-semibold text-[#2C1810]">{title}</h2>
          {sub && <p className="mt-0.5 text-sm text-[#6E5549]">{sub}</p>}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

const btn = "inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[#2C1810] px-6 text-sm font-semibold text-[#FDFBF7] transition-colors hover:bg-[#3D2317] disabled:opacity-50";

function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const run = async (fn) => {
    setBusy(true);
    setMsg(null);
    try {
      const text = await fn();
      setMsg({ ok: true, text });
      return true;
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, msg, run, setMsg };
}

function Note({ msg, testId }) {
  if (!msg) return null;
  return <p role={msg.ok ? "status" : "alert"} data-testid={`${testId}-${msg.ok ? "success" : "error"}`} className={`mt-4 rounded-xl px-4 py-3 text-sm ${msg.ok ? "bg-[#E7F1EA] text-[#2E6B4F]" : "bg-[#F9ECEC] text-[#8C2A2A]"}`}>{msg.text}</p>;
}

function EmailSection() {
  const { user } = useAuth();
  const [f, setF] = useState({ new_email: "", current_password: "" });
  const { busy, msg, run } = useSubmit();
  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await api.post("/auth/change-email", f);
      setF({ new_email: "", current_password: "" });
      return data.message;
    });
  };
  return (
    <Section icon={Mail} title="Email address" sub={<>You sign in with <b data-testid="account-current-email">{user.email}</b>.</>} testId="account-email-section">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
        <label className="block"><span className={labelCls}>New email</span>
          <Input type="email" required autoComplete="email" value={f.new_email} onChange={(e) => setF({ ...f, new_email: e.target.value })} className={`${fieldCls} mt-1.5`} data-testid="account-new-email-input" /></label>
        <div><span className={labelCls}>Current password</span>
          <div className="mt-1.5"><PasswordInput id="account-email-password-input" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} /></div></div>
        <div className="sm:col-span-2">
          <button disabled={busy} className={btn} data-testid="account-change-email-btn">{busy && <Loader2 size={15} className="animate-spin" />} Send confirmation link</button>
          <p className="mt-2 text-xs text-[#A88B7D]">We'll email a link to the new address. Your email only changes after you click it, and we'll let your current address know.</p>
        </div>
      </form>
      <Note msg={msg} testId="account-email" />
    </Section>
  );
}

function PasswordSection() {
  const empty = { current_password: "", new_password: "", confirm: "" };
  const [f, setF] = useState(empty);
  const { busy, msg, run, setMsg } = useSubmit();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return setMsg({ ok: false, text: "The new passwords don't match." });
    run(async () => {
      const { data } = await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      setF(empty);
      return data.message;
    });
  };
  return (
    <Section icon={KeyRound} title="Password" sub="At least 8 characters, with a letter and a number." testId="account-password-section">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-3">
        <div><span className={labelCls}>Current password</span><div className="mt-1.5"><PasswordInput id="account-current-password-input" value={f.current_password} onChange={set("current_password")} /></div></div>
        <div><span className={labelCls}>New password</span><div className="mt-1.5"><PasswordInput id="account-new-password-input" autoComplete="new-password" value={f.new_password} onChange={set("new_password")} /></div></div>
        <div><span className={labelCls}>Confirm new password</span><div className="mt-1.5"><PasswordInput id="account-confirm-password-input" autoComplete="new-password" value={f.confirm} onChange={set("confirm")} /></div></div>
        <div className="sm:col-span-3">
          <button disabled={busy} className={btn} data-testid="account-change-password-btn">{busy && <Loader2 size={15} className="animate-spin" />} Update password</button>
          <p className="mt-2 text-xs text-[#A88B7D]">You'll stay signed in here. Other devices will be signed out.</p>
        </div>
      </form>
      <Note msg={msg} testId="account-password" />
    </Section>
  );
}

function CardSection({ b, onPortal, busy }) {
  const { data, isLoading } = useQuery({ queryKey: ["billing-card"], queryFn: async () => (await api.get("/billing/card")).data, enabled: !!b.has_customer });
  const c = data?.card;
  const noCard = b.partner?.active ? "PMU Accelerator partner access is complimentary, so no card is needed." : "No card on file yet. You'll add one when you start your membership.";
  return (
    <Section icon={CreditCard} title="Billing & card" sub="Card changes, billing address and invoices are handled securely by Stripe." testId="account-card-section">
      {b.has_customer ? (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-[#4A3B32]" data-testid="account-card-summary">
            {isLoading ? "Loading card…" : c ? <><b className="capitalize">{c.brand}</b> ending in <b>{c.last4}</b> · expires {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}</> : data?.error || "No card saved."}
          </p>
          <button onClick={onPortal} disabled={busy} className={btn} data-testid="account-update-card-btn">{busy && <Loader2 size={15} className="animate-spin" />} Update card / billing details</button>
        </div>
      ) : <p className="text-sm text-[#6E5549]" data-testid="account-card-none">{noCard}</p>}
    </Section>
  );
}

export function AccountSections({ b, onPortal, busy, owner = false }) {
  return (
    <div className="mt-10 space-y-6" data-testid="account-sections">
      <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#9C6218]">Account settings</p>
      <EmailSection />
      <PasswordSection />
      {!owner && <CardSection b={b} onPortal={onPortal} busy={busy} />}
    </div>
  );
}
